/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DPC.REQUESTS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DpcRequestsPage.tsx: the Data
 * Privacy & Compliance subject-access-request (DSAR) inbox — raise requests and
 * fulfil them. A DISTINCT presentation (dense card list + status facet chips +
 * bottom-sheet detail + raise-request sheet) over the SAME data/logic layer only:
 *   - reads via apiDb (same dpc_request table + columns),
 *   - raising a request routes through the Common Routine (routedInsert) — §2,
 *   - fulfil / reject go through the module's sanctioned admin-gated RPC
 *     dpc_fulfill_request, exactly like the desktop page (never a direct write).
 * request_type and status are CHECK enums, so their labels come from i18n
 * labelled constants (§17.B11) — never the bare stored literal — and dates go
 * through useFormatDateTime (§11). It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Plus, Check, Ban, FileSearch } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert } from '../../lib/routed_writes';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobileLeadTone, MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DPC.REQUESTS';

type ReqType = 'ACCESS' | 'EXPORT' | 'ERASURE' | 'RECTIFY' | 'RESTRICT';

interface DpcRequestRow {
  dpc_request_id: number;
  request_type: ReqType;
  subject_entity: string;
  subject_id: string;
  status: string;
  result: unknown;
  created_at: string;
}

// Request workflow statuses (CHECK enum) → localized label + status-pill + lead
// tint (§17.B11 — the stored value stays the code; only the label is localized).
interface StatusMeta { key: string; fallback: string; pill: MobilePillTone; lead: MobileLeadTone }
const STATUS_META: Record<string, StatusMeta> = {
  OPEN:        { key: 'mobile.dpc.status.OPEN',        fallback: 'Open',        pill: 'info', lead: 'blue' },
  IN_PROGRESS: { key: 'mobile.dpc.status.IN_PROGRESS', fallback: 'In progress', pill: 'wait', lead: 'amber' },
  FULFILLED:   { key: 'mobile.dpc.status.FULFILLED',   fallback: 'Fulfilled',   pill: 'ok',   lead: 'green' },
  REJECTED:    { key: 'mobile.dpc.status.REJECTED',    fallback: 'Rejected',    pill: 'no',   lead: 'red' },
};
const FALLBACK_STATUS: StatusMeta = { key: '', fallback: '', pill: 'info', lead: 'slate' };

// Request types (CHECK enum) → localized labels for the facet + raise-request select.
const TYPE_META: { value: ReqType; key: string; fallback: string }[] = [
  { value: 'ACCESS',   key: 'mobile.dpc.type.ACCESS',   fallback: 'Access' },
  { value: 'EXPORT',   key: 'mobile.dpc.type.EXPORT',   fallback: 'Export' },
  { value: 'ERASURE',  key: 'mobile.dpc.type.ERASURE',  fallback: 'Erasure' },
  { value: 'RECTIFY',  key: 'mobile.dpc.type.RECTIFY',  fallback: 'Rectify' },
  { value: 'RESTRICT', key: 'mobile.dpc.type.RESTRICT', fallback: 'Restrict' },
];

type StatusFacet = 'all' | 'OPEN' | 'IN_PROGRESS' | 'FULFILLED' | 'REJECTED';

interface RpcResult { ok: boolean; error_code?: string; status?: string }

export function MobileDpcRequestsPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');

  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<DpcRequestRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<DpcRequestRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Raise-request form fields.
  const [cType, setCType] = useState<ReqType>('ACCESS');
  const [cEntity, setCEntity] = useState('');
  const [cId, setCId] = useState('');

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('dpc_request')
        .select('dpc_request_id, request_type, subject_entity, subject_id, status, result, created_at')
        .order('created_at', { ascending: false })
        .limit(100);
      if (e) throw e;
      setRows((data ?? []) as DpcRequestRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const typeLabel = (v: string): string => {
    const meta = TYPE_META.find((m) => m.value === v);
    return meta ? t(meta.key, meta.fallback) : v;
  };
  const statusMeta = (s: string): StatusMeta => STATUS_META[s] ?? { ...FALLBACK_STATUS, fallback: s };
  const statusLabel = (s: string): string => {
    const meta = STATUS_META[s];
    return meta ? t(meta.key, meta.fallback) : s;
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet !== 'all' && r.status !== statusFacet) return false;
      if (!q) return true;
      return (
        typeLabel(r.request_type).toLowerCase().includes(q) ||
        r.request_type.toLowerCase().includes(q) ||
        r.subject_entity.toLowerCase().includes(q) ||
        r.subject_id.toLowerCase().includes(q) ||
        statusLabel(r.status).toLowerCase().includes(q)
      );
    });
    // typeLabel/statusLabel read only from the stable TYPE_META/STATUS_META
    // constants and `t`; excluded from deps to avoid identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, statusFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dpc.all', 'All') },
    { key: 'OPEN', label: t('mobile.dpc.status.OPEN', 'Open') },
    { key: 'IN_PROGRESS', label: t('mobile.dpc.status.IN_PROGRESS', 'In progress') },
    { key: 'FULFILLED', label: t('mobile.dpc.status.FULFILLED', 'Fulfilled') },
    { key: 'REJECTED', label: t('mobile.dpc.status.REJECTED', 'Rejected') },
  ];

  const isActionable = (s: string) => s === 'OPEN' || s === 'IN_PROGRESS';

  const fulfil = async (id: number, decision: 'FULFILL' | 'REJECT') => {
    if (decision === 'FULFILL' && !window.confirm(
      t('mobile.dpc.confirm_fulfil', 'Fulfil this request? ERASURE permanently anonymizes the subject data.'),
    )) return;
    setActionBusy(true);
    setError(null);
    try {
      // Module's sanctioned admin-gated RPC (mirrors the desktop) — not a direct write.
      const { data, error: e } = await apiDb.rpc('dpc_fulfill_request', { p_request_id: id, p_decision: decision });
      if (e) throw e;
      const res = data as RpcResult | null;
      if (!res?.ok) throw new Error(res?.error_code ?? 'DPC.FULFIL_FAILED');
      setSelected(null);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const create = async () => {
    if (!cEntity.trim() || !cId.trim()) {
      setError(t('mobile.dpc.subject_required', 'Subject entity and id are required.'));
      return;
    }
    setActionBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedInsert(session, 'dpc_request', {
      request_type: cType,
      subject_entity: cEntity.trim(),
      subject_id: cId.trim(),
      status: 'OPEN',
    });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setCEntity('');
    setCId('');
    setCType('ACCESS');
    setCreating(false);
    void reload();
  };

  const resultText = (result: unknown): string => {
    try {
      return JSON.stringify(result, null, 2);
    } catch {
      return String(result);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.dpc.requests.title', 'Privacy Requests')}
        description={t('mobile.dpc.requests.description', 'Subject access requests to review, fulfil or reject.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.dpc.stat.total', 'Total') },
          { value: rows.filter((r) => r.status === 'OPEN').length, label: t('mobile.dpc.stat.open', 'Open') },
          { value: rows.filter((r) => r.status === 'FULFILLED').length, label: t('mobile.dpc.stat.fulfilled', 'Fulfilled') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.dpc.search', 'Search requests')}
          aria-label={t('mobile.dpc.search', 'Search requests')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {canAdd && (<button
        type="button"
        className="m-btn m-btn-primary"
        style={{ margin: '6px 0 4px' }}
        onClick={() => { setError(null); setCreating(true); }}
      >
        <Plus size={16} /> {t('mobile.dpc.raise', 'Raise request')}
      </button>)}

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dpc.none', 'No requests match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.dpc_request_id}
              lead={<FileSearch />}
              leadTone={statusMeta(r.status).lead}
              title={typeLabel(r.request_type)}
              subtitle={`${r.subject_entity} · ${r.subject_id}`}
              trailing={<MobileStatusPill tone={statusMeta(r.status).pill}>{statusLabel(r.status)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.dpc.detail', 'Request detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><FileSearch size={20} /></span>
              <span>
                <div className="m-nm">{typeLabel(selected.request_type)}</div>
                <div className="m-em">{selected.subject_entity} · {selected.subject_id}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.dpc.status_label', 'Status')}
                value={<MobileStatusPill tone={statusMeta(selected.status).pill}>{statusLabel(selected.status)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.dpc.type_label', 'Type')} value={typeLabel(selected.request_type)} />
              <MobileDetailRow label={t('mobile.dpc.subject_entity', 'Subject entity')} value={selected.subject_entity} />
              <MobileDetailRow label={t('mobile.dpc.subject_id', 'Subject id')} value={selected.subject_id} />
              <MobileDetailRow label={t('mobile.dpc.created', 'Created')} value={formatDateTime(selected.created_at)} />
              {selected.result != null && (
                <div className="m-formrow">
                  <label>{t('mobile.dpc.result', 'Result')}</label>
                  <div className="m-formrow-ro" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                    {resultText(selected.result)}
                  </div>
                </div>
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              {isActionable(selected.status) && (
                <>
                  <button
                    type="button"
                    className="m-btn m-btn-primary"
                    disabled={actionBusy}
                    onClick={() => void fulfil(selected.dpc_request_id, 'FULFILL')}
                  >
                    <Check size={16} />
                    {t('mobile.dpc.fulfil', 'Fulfil')}
                  </button>
                  <button
                    type="button"
                    className="m-btn m-btn-danger"
                    disabled={actionBusy}
                    onClick={() => void fulfil(selected.dpc_request_id, 'REJECT')}
                  >
                    <Ban size={16} />
                    {t('mobile.dpc.reject', 'Reject')}
                  </button>
                </>
              )}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Raise-request bottom-sheet */}
      <div className={'m-scrim' + (creating ? ' m-open' : '')} onClick={() => { if (!actionBusy) setCreating(false); }} />
      <div className={'m-sheet' + (creating ? ' m-open' : '')} aria-label={t('mobile.dpc.raise', 'Raise request')}>
        <div className="m-grabber" />
        {creating && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><FileSearch size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.dpc.raise', 'Raise request')}</div>
                <div className="m-em">{t('mobile.dpc.raise_hint', 'New subject access request')}</div>
              </span>
            </div>
            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-dpc-type">{t('mobile.dpc.type_label', 'Type')}</label>
                <select id="m-dpc-type" className="m-sel" value={cType} onChange={(e) => setCType(e.target.value as ReqType)}>
                  {TYPE_META.map((o) => <option key={o.value} value={o.value}>{t(o.key, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dpc-entity">{t('mobile.dpc.subject_entity', 'Subject entity')}</label>
                <input
                  id="m-dpc-entity"
                  className="m-inp"
                  value={cEntity}
                  onChange={(e) => setCEntity(e.target.value)}
                  placeholder={t('mobile.dpc.subject_entity_ph', 'e.g. eds_recipient')}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dpc-id">{t('mobile.dpc.subject_id', 'Subject id')}</label>
                <input
                  id="m-dpc-id"
                  className="m-inp"
                  value={cId}
                  onChange={(e) => setCId(e.target.value)}
                  placeholder={t('mobile.dpc.subject_id_ph', 'subject id')}
                />
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => void create()}>
                {actionBusy ? t('common.saving', 'Saving…') : t('mobile.dpc.raise', 'Raise request')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setCreating(false)} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
